import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationService } from './application.service';
import { ApiService } from './api.service';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { BaseService } from './base-service';
import { UserService } from './user.service';

interface ExportResponse {
    status: string;
    message: string;
}

@Injectable({
    providedIn: 'root',
})
export class DownloadService extends BaseService {
    isEnergyReportLoading$ = new BehaviorSubject<boolean>(false);
    isExportLoading$ = new BehaviorSubject<boolean>(false);
    isThirdPartyExportLoading$ = new BehaviorSubject<boolean>(false);
    isGdprExportLoading$ = new BehaviorSubject<boolean>(false);
    private readonly ENERGY_REPORT_URL = 'https://ews.n2g-iona.net/energy-report?period=';

    constructor(
        private toastr: ToastrService,
        private translate: TranslateService,
        private application: ApplicationService,
        private apiService: ApiService,
        protected http: HttpClient,
        protected auth: ApiService,
        protected user: UserService
    ) {
        super(http, auth, user);
    }

    /** Open energy report */
    openEnergyReport(month: string) {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return;
        }

        this.isEnergyReportLoading$.next(true);
        const token = this.apiService.getToken();

        if (!token) {
            this.toastr.error(this.translate.instant('common.errors.generalError'));
            this.isEnergyReportLoading$.next(false);
            return;
        }

        const timestamp = Date.now();
        const labelpartner = this.user.getActiveUserProvider();

        const url = `${this.ENERGY_REPORT_URL}${month}&token=${encodeURIComponent(token)}&labelpartner=${encodeURIComponent(labelpartner)}&ts=${timestamp}`;
        window.open(url, '_blank');

        this.isEnergyReportLoading$.next(false);
    }

    /** Export consumption data */
    exportConsumptionData(selectedMonth: string) {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return EMPTY;
        }

        if (!selectedMonth) {
            return EMPTY;
        }

        this.isExportLoading$.next(true);

        const [year, month] = selectedMonth.split('-');
        const fromDate = `${year}-${month}-01`;
        const lastDay = new Date(+year, +month, 0).getDate();
        const toDate = `${year}-${month}-${lastDay}`;

        const url = `${this.EWS_BASE_URL}/data-export/consumption/electricity/quarter-hours/${fromDate}/${toDate}/export?output=csv`;
        const token = this.apiService.getToken();

        if (!token) {
            this.toastr.error(this.translate.instant('common.errors.generalError'));
            this.isExportLoading$.next(false);
            return EMPTY;
        }

        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get(url, { headers, responseType: 'blob' }).pipe(
            tap({
                next: (blob: Blob) => {
                    const blobUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = `stromverbrauch_${selectedMonth}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                    this.toastr.success(this.translate.instant('screens.downloads.export.success'));
                },
                error: (err) => {
                    console.error('Error exporting consumption data:', err);
                    this.toastr.error(this.translate.instant('common.errors.generalError'));
                },
            }),
            finalize(() => this.isExportLoading$.next(false))
        );
    }

    /** Export to third parties */
    exportToThirdParties() {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return EMPTY;
        }

        this.isThirdPartyExportLoading$.next(true);
        const token = this.apiService.getToken();

        if (!token) {
            this.toastr.error(this.translate.instant('common.errors.generalError'));
            this.isThirdPartyExportLoading$.next(false);
            return EMPTY;
        }

        const url = `${this.API_BASE_URL}export?export_reason=EU_DATA_ACT_EXPORT`;
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get<ExportResponse>(url, { headers }).pipe(
            tap({
                next: (res) => {
                    if (res && res.status === 'accepted') {
                        this.toastr.warning(
                            this.translate.instant(
                                'screens.downloads.exportToThirdParties.cooldown'
                            ),
                            '',
                            {
                                timeOut: 10000,
                                closeButton: true,
                            }
                        );
                    } else {
                        this.toastr.success(
                            this.translate.instant(
                                'screens.downloads.exportToThirdParties.success'
                            ),
                            '',
                            {
                                timeOut: 10000,
                                closeButton: true,
                            }
                        );
                    }
                },
                error: (err) => {
                    console.error('Error exporting to third parties:', err);
                    this.toastr.error(
                        this.translate.instant('screens.downloads.exportToThirdParties.error')
                    );
                },
            }),
            finalize(() => this.isThirdPartyExportLoading$.next(false))
        );
    }

    /** Export GDPR data */
    exportGdprData() {
        if (this.application.isDemoMode()) {
            this.toastr.info(this.translate.instant('common.demoModeInfo'));
            return EMPTY;
        }

        this.isGdprExportLoading$.next(true);
        const token = this.apiService.getToken();

        if (!token) {
            this.toastr.error(this.translate.instant('common.errors.generalError'));
            this.isGdprExportLoading$.next(false);
            return EMPTY;
        }

        const url = `${this.API_BASE_URL}export?export_reason=GDPR_EXPORT`;
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get<ExportResponse>(url, { headers }).pipe(
            tap({
                next: (res) => {
                    if (res && res.status === 'accepted') {
                        this.toastr.warning(
                            this.translate.instant('screens.downloads.gdprExport.cooldown'),
                            '',
                            {
                                timeOut: 10000,
                                closeButton: true,
                            }
                        );
                    } else {
                        this.toastr.success(
                            this.translate.instant('screens.downloads.gdprExport.success'),
                            '',
                            {
                                timeOut: 10000,
                                closeButton: true,
                            }
                        );
                    }
                },
                error: (err) => {
                    console.error('Error exporting GDPR data:', err);
                    this.toastr.error(this.translate.instant('screens.downloads.gdprExport.error'));
                },
            }),
            finalize(() => this.isGdprExportLoading$.next(false))
        );
    }
}
